import { INVOICE_REFERENCE, INVOICES } from "../../api/endpoints";
import { baseApi, listTags } from "./baseApi";

const multipart =
  typeof FormData !== "undefined"
    ? { headers: { "Content-Type": "multipart/form-data" } }
    : {};

export const invoicesApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getInvoices: build.query({
      query: (params = {}) => ({ url: INVOICES.LIST, method: "GET", params }),
      providesTags: (result) => listTags("Invoice", result),
    }),
    getGenerateInvoicePDF: build.query({
      query: ({ id } = {}) => ({
        url: INVOICES.GENERATE_INVOICE_PDF(id),
        method: "GET",
        responseType: "blob",
        headers: { Accept: "application/pdf" },
      }),
    }),
    getInvoiceById: build.query({
      query: ({ id, params } = {}) => ({
        url: INVOICES.BY_ID(id),
        method: "GET",
        params,
      }),
      providesTags: (_r, _e, arg) => [{ type: "Invoice", id: arg?.id ?? arg }],
    }),
    createInvoice: build.mutation({
      query: (arg) => {
        const isWrapped =
          arg &&
          typeof arg === "object" &&
          !(arg instanceof FormData) &&
          ("data" in arg || "params" in arg);
        const payload = isWrapped ? arg.data : arg;
        const params = isWrapped ? arg.params : undefined;
        const isFormData =
          typeof FormData !== "undefined" && payload instanceof FormData;
        return {
          url: INVOICES.LIST,
          method: "POST",
          data: payload,
          params,
          ...(isFormData ? multipart : {}),
        };
      },
      invalidatesTags: [{ type: "Invoice", id: "LIST" }],
    }),
    updateInvoice: build.mutation({
      query: ({ id, data: updateData, params, ...rest }) => {
        const payload = updateData ?? rest;
        const isFormData =
          typeof FormData !== "undefined" && payload instanceof FormData;
        return {
          url: INVOICES.BY_ID(id),
          method: "PUT",
          data: payload,
          params,
          ...(isFormData ? multipart : {}),
        };
      },
      invalidatesTags: (_r, _e, { id }) => [
        { type: "Invoice", id },
        { type: "Invoice", id: "LIST" },
      ],
    }),
    deleteInvoice: build.mutation({
      query: (id) => ({ url: INVOICES.BY_ID(id), method: "DELETE" }),
      invalidatesTags: (_r, _e, id) => [
        { type: "Invoice", id },
        { type: "Invoice", id: "LIST" },
      ],
    }),
    recreateInstallmentToken: build.mutation({
      query: ({ id, data: formData }) => {
        const isFormData =
          typeof FormData !== "undefined" && formData instanceof FormData;
        return {
          url: INVOICES.RECREATE_INSTALLMENT_TOKEN(id),
          method: "POST",
          data: formData,
          ...(isFormData ? multipart : {}),
        };
      },
      invalidatesTags: (_r, _e, { id }) => [
        { type: "Invoice", id },
        { type: "Invoice", id: "LIST" },
      ],
    }),
    getCurrencies: build.query({
      query: (params = {}) => ({
        url: INVOICE_REFERENCE.CURRENCIES,
        method: "GET",
        params,
      }),
      providesTags: [{ type: "Lookup", id: "CURRENCIES" }],
    }),
    getPaymentTypes: build.query({
      query: (params = {}) => ({
        url: INVOICE_REFERENCE.PAYMENT_TYPES,
        method: "GET",
        params,
      }),
      providesTags: [{ type: "Lookup", id: "PAYMENT_TYPES" }],
    }),
    getPaymentAccounts: build.query({
      query: (params = {}) => ({
        url: INVOICE_REFERENCE.ACCOUNTS,
        method: "GET",
        params,
      }),
      providesTags: (result) => listTags("PaymentAccount", result),
    }),
    getPaymentGateways: build.query({
      query: (params = {}) => ({
        url: INVOICE_REFERENCE.PAYMENT_GATEWAYS,
        method: "GET",
        params,
      }),
      providesTags: [{ type: "Lookup", id: "PAYMENT_GATEWAYS" }],
    }),
    getPaymentAccountById: build.query({
      query: (id) => ({ url: INVOICE_REFERENCE.BY_ID(id), method: "GET" }),
      providesTags: (_r, _e, id) => [{ type: "PaymentAccount", id }],
    }),
    createPaymentAccount: build.mutation({
      query: (body) => ({
        url: INVOICE_REFERENCE.ACCOUNTS,
        method: "POST",
        data: body,
      }),
      invalidatesTags: [{ type: "PaymentAccount", id: "LIST" }],
    }),
    updatePaymentAccount: build.mutation({
      query: ({ id, ...body }) => ({
        url: INVOICE_REFERENCE.BY_ID(id),
        method: "PUT",
        data: body,
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: "PaymentAccount", id },
        { type: "PaymentAccount", id: "LIST" },
      ],
    }),
    deletePaymentAccount: build.mutation({
      query: (id) => ({ url: INVOICE_REFERENCE.BY_ID(id), method: "DELETE" }),
      invalidatesTags: (_r, _e, id) => [
        { type: "PaymentAccount", id },
        { type: "PaymentAccount", id: "LIST" },
      ],
    }),
  }),
});

export const {
  useGetInvoicesQuery,
  useLazyGetInvoicesQuery,
  useGetGenerateInvoicePDFQuery,
  useLazyGetGenerateInvoicePDFQuery,
  useGetInvoiceByIdQuery,
  useLazyGetInvoiceByIdQuery,
  useCreateInvoiceMutation,
  useUpdateInvoiceMutation,
  useDeleteInvoiceMutation,
  useRecreateInstallmentTokenMutation,
  useGetCurrenciesQuery,
  useLazyGetCurrenciesQuery,
  useGetPaymentTypesQuery,
  useLazyGetPaymentTypesQuery,
  useGetPaymentAccountsQuery,
  useLazyGetPaymentAccountsQuery,
  useGetPaymentGatewaysQuery,
  useLazyGetPaymentGatewaysQuery,
  useGetPaymentAccountByIdQuery,
  useLazyGetPaymentAccountByIdQuery,
  useCreatePaymentAccountMutation,
  useUpdatePaymentAccountMutation,
  useDeletePaymentAccountMutation,
} = invoicesApi;
